import {
  countPolarity,
  POLARITY_LABELS,
  type EvaluationAxisDef,
  type EvaluationPolarity,
} from '@citizen-walk/shared-types';
import type { AreaBarChartAxis } from './AreaBarChart';
import { resolveMasterRow } from './streetSurveyPinDisplay';
import type { PrimaryAxisResolution } from './streetSurveyAxisTypes';
import type { StreetSurveyPost } from './useStreetSurveyPosts';

/**
 * `AreaReportModal.tsx` の役割ごと検出件数集計ロジック (2026-07-16実装) を切り出したもの。
 * エリア選択集計・路線調査の保存 (docs/25 §10-5/§10-6) の両方から使う共通処理。
 *
 * docs/27 §9 (2026-09-07): 集計軸は呼出側が選んだ「調査項目」(primaryAxis 形で渡す)。グループ化は
 * マスタの `colorAxis` に従い (major / majorCd = 大分類、category / categoryCd = 細分類)、評価軸
 * (`evaluation`) があれば各グループにポジ / ネガ / 中立の内訳を付ける。
 */

/**
 * 集計の細かさ (2026-09-16、オーナー指摘「12 の質的基準ごとに集計したいのに大項目でしか集計できない」)。
 *   - `auto`: マスタの `colorAxis` に従う (従来の挙動。walker-gql は majorCd = 大分類)
 *   - `major`: 大分類でまとめる
 *   - `category`: 細分類 (ゲール基準なら 12 の質的基準) ごと
 */
export type AxisGranularity = 'auto' | 'major' | 'category';

/** 実際に使うグループ化の種類 (colorAxis と細かさの指定から決める)。 */
function resolveGrouping(
  colorAxis: string | undefined,
  granularity: AxisGranularity,
): 'major' | 'category' {
  if (granularity === 'major') return 'major';
  if (granularity === 'category') return 'category';
  return colorAxis === 'category' || colorAxis === 'categoryCd' ? 'category' : 'major';
}

/** マスタの colorAxis 規約 (と集計の細かさ) に沿ってグループ化キーを決める。 */
export function groupKeyOf(
  row: { major?: string; majorCd?: string; category?: string; categoryCd: string },
  colorAxis: string | undefined,
  granularity: AxisGranularity = 'auto',
): string {
  if (resolveGrouping(colorAxis, granularity) === 'category') {
    // 細分類は大分類をまたいで番号が重複しうるので、大分類があれば付けて一意にする。
    if (granularity === 'auto' && colorAxis === 'category') return row.category ?? row.categoryCd;
    return row.majorCd
      ? `${row.majorCd}-${row.categoryCd}`
      : row.categoryCd || (row.category ?? '—');
  }
  if (colorAxis === 'major' && granularity === 'auto') return row.major ?? row.majorCd ?? '—';
  return row.majorCd ?? row.major ?? '—';
}

export function groupLabelOf(
  row: { major?: string; majorCd?: string; category?: string; categoryCd: string },
  colorAxis: string | undefined,
  key: string,
  granularity: AxisGranularity = 'auto',
): string {
  if (resolveGrouping(colorAxis, granularity) === 'category') return row.category ?? key;
  return row.major ?? key;
}

export function groupColorOf(
  master: NonNullable<PrimaryAxisResolution['master']>,
  row: { majorCd?: string; color?: string },
  key: string,
  granularity: AxisGranularity = 'auto',
): string {
  if (resolveGrouping(master.colorAxis, granularity) === 'category') {
    return row.color ?? master.colorMap?.[row.majorCd ?? ''] ?? '#94A3B8';
  }
  return master.colorMap?.[key] ?? master.colorMap?.[row.majorCd ?? ''] ?? row.color ?? '#94A3B8';
}

interface AxisGroup {
  label: string;
  color: string;
  count: number;
  polarity: { positive: number; negative: number; neutral: number };
}

/**
 * 選択地点群の検出件数を、集計軸のマスタの実際の行から動的に集計する (決め打ちしない)。
 * `evaluation` を渡すと各グループに評価の内訳 (`polarity`) を付ける。
 */
export function computeAxisCounts(
  posts: StreetSurveyPost[],
  primaryAxis: PrimaryAxisResolution,
  evaluation?: EvaluationAxisDef | null,
  granularity: AxisGranularity = 'auto',
): AreaBarChartAxis[] {
  const axisGroups = new Map<string, AxisGroup>();
  const master = primaryAxis.master;
  if (master) {
    for (const row of master.rows ?? []) {
      const key = groupKeyOf(row, master.colorAxis, granularity);
      if (!axisGroups.has(key)) {
        axisGroups.set(key, {
          label: groupLabelOf(row, master.colorAxis, key, granularity),
          color: groupColorOf(master, row, key, granularity),
          count: 0,
          polarity: { positive: 0, negative: 0, neutral: 0 },
        });
      }
    }
  }
  for (const post of posts) {
    for (const c of post.classifications) {
      const code = String(c[primaryAxis.primaryAxis?.codeField ?? ''] ?? '');
      const row = resolveMasterRow(code, primaryAxis.byCategoryCd);
      if (!row || !master || row.excludeFromAggregation) continue;
      const key = groupKeyOf(row, master.colorAxis, granularity);
      const entry = axisGroups.get(key);
      if (!entry) continue;
      entry.count += 1;
      if (evaluation) {
        const p = evaluation.polarityOf(c[evaluation.field]);
        if (p) entry.polarity[p] += 1;
      }
    }
  }
  return [...axisGroups.entries()].map(([key, g]) => ({
    key,
    label: g.label,
    value: g.count,
    color: g.color,
    ...(evaluation ? { polarity: g.polarity } : {}),
  }));
}

/**
 * 集計結果を「項目 × 評価の区分」の棒に展開する (2026-09-16、オーナー指示)。
 *
 * 棒の中を塗り分ける表示 (`AreaBarChart` の積み上げ) では、項目ごとの内訳は分かっても評価どうしの
 * 大小を比べにくい。この関数は各項目をポジ / ネガ / 中立の棒に分け、比較グラフ・PDF でもそのまま
 * 使える形 (`AreaBarChartAxis[]`) にする。評価の内訳を持たない集計はそのまま返す。
 */
export function expandAxisCountsByPolarity(
  axes: AreaBarChartAxis[],
  /** 評価の区分の表示名 (評価の定義の `polarityLabels`)。 */
  polarityLabels: Record<EvaluationPolarity, string> = POLARITY_LABELS,
): AreaBarChartAxis[] {
  if (!axes.some((a) => a.polarity)) return axes;
  const out: AreaBarChartAxis[] = [];
  for (const axis of axes) {
    const p = axis.polarity;
    if (!p) {
      out.push(axis);
      continue;
    }
    // 表示順はポジティブ → 中立 → ネガティブ (グラフの積み上げと揃える)。
    for (const polarity of ['positive', 'neutral', 'negative'] as const) {
      out.push({
        key: `${axis.key}__${polarity}`,
        label: `${axis.label}／${polarityLabels[polarity]}`,
        // 色は項目のまま (評価の区分は模様で表す、オーナー指示 2026-09-16)。
        color: axis.color,
        value: p[polarity],
        polarityKind: polarity,
      });
    }
  }
  return out;
}

/**
 * 評価の内訳を 1 行の文字にする (表のセル用)。区分の名前は評価の定義から取る (例: いいね！ 3 / もったいない 1 / 中立 0)。
 */
export function formatPolarityCounts(
  counts: { positive: number; negative: number; neutral: number },
  polarityLabels: Record<EvaluationPolarity, string> = POLARITY_LABELS,
): string {
  return `${polarityLabels.positive} ${counts.positive} / ${polarityLabels.negative} ${counts.negative} / ${polarityLabels.neutral} ${counts.neutral}`;
}

/** 選択地点群の全検出要素の評価内訳 (評価軸が無ければ null)。 */
export function computeEvaluationSummary(
  posts: StreetSurveyPost[],
  evaluation: EvaluationAxisDef | null | undefined,
) {
  if (!evaluation) return null;
  const values: unknown[] = [];
  for (const post of posts) {
    for (const c of post.classifications) values.push(c[evaluation.field]);
  }
  return countPolarity(values, evaluation);
}
